/**
 * What the flow sends: the experiment's own fields, the try-one-case request,
 * and which steps are done. Pure functions over the flow's state; the server
 * re-validates all of it.
 */

import { choiceRange } from "@/features/traces/period-picker";
import { inputMappingPayload } from "./input-mapping";
import { credentialRefsPayload, runSettingsPayload } from "../run-package";
import { evaluatorsPayload } from "../scoring/evaluator-overrides";
import type { EvaluatorChoice, TryCaseRequest } from "../types";
import type { NewExperiment } from "./use-new-experiment";

export const STEPS = ["Name", "Dataset", "Runner", "Evaluators", "Review and test", "Start"];

/** The review step: from here on the experiment can be saved or started. */
export const REVIEW = 4;

/** The index of the first step not yet done: steps after it cannot be opened. */
export function reachable(f: NewExperiment): number {
  if (!f.form.name.trim()) return 0;
  if (!f.datasetId) return 1;
  if (f.runner === "agent" ? !f.connection || !f.target.ready : !f.hasOutputs) return 2;
  if (f.evaluatorIds.length === 0 || f.evaluators.isPending || f.evaluators.isError) return 3;
  if (f.live && f.liveVersion.fresh && !choiceRange(f.period)) return 1;
  return STEPS.length - 1;
}

/** Run settings, credentials and the input mapping, as both the run and a try send them. */
export function runParts(f: NewExperiment) {
  const spec = f.target.spec.data;
  return {
    run_settings: runSettingsPayload(spec, f.form.runSettings),
    credential_refs: credentialRefsPayload(spec, f.form.credentialRefs),
    /** Only inputs with a source; an input left unmapped is not sent. */
    mapped: f.mapping.inputs.length ? inputMappingPayload(f.mapping.mapping) : undefined,
  };
}

/** The ticked evaluators, each with this run's mapping fixes: what a test on one case scores with. */
export function evaluatorChoices(f: NewExperiment): EvaluatorChoice[] {
  const payload = evaluatorsPayload(f.evaluatorIds, f.fixes.overrides);
  return "evaluators" in payload
    ? payload.evaluators
    : payload.evaluator_ids.map((id) => ({ evaluator_id: id }));
}

export function tryRequest(f: NewExperiment, itemId: string | null): TryCaseRequest | null {
  const target = f.target.payload();
  if (!target || !f.datasetId) return null;
  const { mapped, ...parts } = runParts(f);
  return {
    ...target,
    dataset_id: f.datasetId,
    ...(itemId ? { dataset_item_id: itemId } : {}),
    ...parts,
    input_mapping: mapped ?? {},
    evaluators: evaluatorChoices(f),
  };
}

export function experimentFields(
  f: NewExperiment,
  datasetId: string,
  chosen: { evaluator_ids: string[] } | { evaluators: EvaluatorChoice[] },
) {
  const { mapped, ...parts } = runParts(f);
  return {
    name: f.form.name.trim(),
    description: f.form.description.trim(),
    dataset_id: datasetId,
    ...(f.live ? liveFields(f) : {}),
    ...chosen,
    repetitions: f.form.repetitions,
    cost_limit_usd: Number(f.form.costLimit),
    ...parts,
    ...(mapped ? { input_mapping: mapped } : {}),
  };
}

/**
 * A run on a live dataset: an existing version, or fresh traces pulled when it
 * starts (the period, or the next ones after the latest version).
 */
export function liveFields(f: NewExperiment): {
  dataset_version?: number;
  period?: { start: string; end: string };
  continue_from_latest?: true;
} {
  const v = f.liveVersion;
  if (v.version != null) return { dataset_version: v.version };
  const period = choiceRange(f.period);
  if (!period) return {};
  // Continuing ignores the period start and runs up to now: the moment Start is pressed, since
  // a live run is never saved as a draft first (create and start are one request).
  if (v.next) return { period: { ...period, end: new Date().toISOString() }, continue_from_latest: true };
  return { period };
}
